import { get as getStorage, set as setStorage } from '@fluorescent/storage';
import { emit } from 'evx';

import dispatchCustomEvent from '@/lib/dispatch-custom-event';

const routes = window.theme.routes.cart || {};

const paths = {
  base: `${routes.base || '/cart'}.js`,
  add: `${routes.add || '/cart/add'}.js`,
  change: `${routes.change || '/cart/change'}.js`,
  clear: `${routes.clear || '/cart/clear'}.js`,
};

// Add a `sorted` key that orders line items
// in the order the customer added them if possible
function sortCart(cart) {
  const order = getStorage('cart_order') || [];

  if (order.length) {
    cart.sorted = [...cart.items].sort(
      (a, b) => order.indexOf(a.variant_id) - order.indexOf(b.variant_id)
    );
    return cart;
  }

  cart.sorted = cart.items;
  return cart;
}

function addVariant(variant, quantity) {
  const numAvailable =
    variant.inventory_policy === 'deny' &&
    variant.inventory_management === 'shopify'
      ? variant.inventory_quantity
      : null; // null means they can add as many as they want

  return get().then(({ items }) => {
    const existing = items.filter(item => item.id === variant.id)[0] || {};
    const numRequested = (existing.quantity || 0) + quantity;

    if (numAvailable !== null && numRequested > numAvailable) {
      const err = `There are only ${numAvailable} of that product available, requested ${numRequested}.`;
      throw new Error(err);
    } else {
      return addItemById(variant.id, quantity);
    }
  });
}

export function updateItem(id, quantity) {
  return get().then(({ items }) => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].variant_id === parseInt(id)) {
        return changeItem(i + 1, quantity); // shopify cart is a 1-based index
      }
    }
  });
}

function changeItem(line, quantity) {
  return fetch(paths.change, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ line, quantity }),
  })
    .then(res => res.json())
    .then(cart => {
      emit('cart:updated', { cart: sortCart(cart) });
      return sortCart(cart);
    });
}

function addItemById(id, quantity) {
  emit('cart:updating');

  return fetch(paths.add, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, quantity }),
  })
    .then(r => r.json())
    .then(item => {
      return get().then(cart => {
        const order = getStorage('cart_order') || [];
        const newOrder = [
          item.variant_id,
          ...order.filter(i => i !== item.variant_id),
        ];
        setStorage('cart_order', JSON.stringify(newOrder));

        emit('cart:updated', { cart: sortCart(cart) });
        return { item, cart: sortCart(cart) };
      });
    });
}

function get() {
  return fetch(paths.base, {
    method: 'GET',
    credentials: 'include',
  })
    .then(res => res.json())
    .then(data => {
      return sortCart(data);
    });
}

function addItem(form) {
  emit('cart:updating');

  return fetch(paths.add, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: serialize(form),
  })
    .then(r => r.json())
    .then(res => {
      if (res.status == '422') {
        const errorMessage = { code: 422, message: res.description };
        dispatchCustomEvent('cart:error', { errorMessage: res.description });
        throw errorMessage;
      }

      return get().then(cart => {
        const order = getStorage('cart_order') || [];
        const newOrder = [
          res.variant_id,
          ...order.filter(i => i !== res.variant_id),
        ];
        setStorage('cart_order', JSON.stringify(newOrder));

        emit('cart:updated', { cart: sortCart(cart) });
        dispatchCustomEvent('cart:updated', { cart: sortCart(cart) });
        return { item: res, cart: sortCart(cart) };
      });
    });
}

// !
//  Serialize all form data into a SearchParams string
//  (c) 2020 Chris Ferdinandi, MIT License, https://gomakethings.com
//  @param  {Node}   form The form to serialize
//  @return {String}      The serialized form data
//
function serialize(form) {
  var arr = [];
  Array.prototype.slice.call(form.elements).forEach(function (field) {
    if (
      !field.name ||
      field.disabled ||
      ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
    ) {
      return;
    }
    if (field.type === 'select-multiple') {
      Array.prototype.slice.call(field.options).forEach(function (option) {
        if (!option.selected) return;
        arr.push(
          encodeURIComponent(field.name) +
            '=' +
            encodeURIComponent(option.value)
        );
      });
      return;
    }
    if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked) {
      return;
    }
    arr.push(
      encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
    );
  });
  return arr.join('&');
}

export default { addItem, addItemById, addVariant, get, updateItem };
